import type { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js'
import React, { useContext, useState } from 'react'
import { EnvironmentInfo, fetchEnvironment } from 'admin-web-utils'
import { useAsync } from 'react-use'
import { SimplePage } from '../../components/SimplePage'
import { LoadingOutlined } from '@ant-design/icons'
import { createUserPool } from '../../create-user-pool'

export interface AppState {
  environment: EnvironmentInfo
  featureFlags: string[]
  cognitoUserPool: CognitoUserPool
  cognitoUser: CognitoUser | null
  setCognitoUser: (user: CognitoUser | null) => void
}

const Context = React.createContext<AppState>(undefined as never)

export const useAppContext = (): AppState => useContext(Context)

/**
 * The <AppContext> container provides the minimum context needed
 * to launch the app, including the environment config
 * (obtained via `environment.json`) and the Cognito objects
 * needed for sign in, sign out, etc.
 *
 * This container's routes mainly deal with functionality
 * available to users that are not signed in.
 *   E.g. login and forgot-password.
 *
 * There is no access to the graphql clients at the
 * App container level. This is provided further down
 * in the container hierarchy (see <Console> and <Project>).
 */
interface Props {
  children?: React.ReactElement
}

export const AppContext: React.FC<Props> = (props) => {
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>()

  const initState = useAsync(async () => {
    const environmentInfo = await fetchEnvironment()
    const featureFlags = environmentInfo.featureFlags
    const cognitoPool = createUserPool(
      environmentInfo.userPoolId,
      environmentInfo.userPoolClientId,
    )
    const cognitoUser = cognitoPool.getCurrentUser()

    setCognitoUser(cognitoUser)

    return {
      environmentInfo,
      featureFlags,
      cognitoPool,
    }
  })

  if (initState.error) {
    return <SimplePage>Error loading app. Please refresh the page.</SimplePage>
  } else if (
    initState.loading ||
    !initState.value ||
    cognitoUser === undefined
  ) {
    return (
      <SimplePage>
        <LoadingOutlined />
      </SimplePage>
    )
  } else {
    return (
      <Context.Provider
        value={{
          environment: initState.value.environmentInfo,
          featureFlags: initState.value.featureFlags,
          cognitoUserPool: initState.value.cognitoPool,
          cognitoUser,
          setCognitoUser,
        }}
      >
        {props.children}
      </Context.Provider>
    )
  }
}
